import { For, createResource, createSignal } from "solid-js";
import { A } from "@solidjs/router";

import AsyncResource from "../components/AsyncResource";
import PageHeader from "../components/PageHeader";
import CursorPager from "../components/CursorPager";
import { needsFirm } from "../components/FirmPicker";
import { api, type ChallengeListCursor } from "../lib/api";
import { createCursorPages } from "../lib/cursorPages";
import { useSession } from "../lib/session";
import { firmWords } from "../lib/orgWords";

const PAGE_SIZE = 50;
const STATUSES = [
  "",
  "active",
  "pending_review",
  "passed",
  "failed",
  "expired",
  "reset",
  "suspended",
  "completed",
];

export default function ChallengesList() {
  const session = useSession();
  const words = () => firmWords(session.admin());
  const pages = createCursorPages<ChallengeListCursor>();
  const [status, setStatus] = createSignal<string>("");
  const [globalUserId, setGlobalUserId] = createSignal<string>("");

  const [page] = createResource(
    () =>
      !needsFirm(session.admin()?.role ?? "") && {
        cursor: pages.current(),
        status: status() || null,
        global_user_id: globalUserId() || null,
      },
    ({ cursor, status, global_user_id }) =>
      api.listChallenges({
        limit: PAGE_SIZE,
        status: status ?? undefined,
        global_user_id: global_user_id ?? undefined,
        ...cursor,
      }),
  );

  return (
    <>
      <PageHeader title={words()?.area ?? ""} subtitle="Evaluation + funded runs" />

      <div class="card mb-4">
        <div class="card-body grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label class="label" for="status-filter">
              Status
            </label>
            <select
              id="status-filter"
              class="select"
              value={status()}
              onInput={(e) => {
                setStatus(e.currentTarget.value);
                pages.reset();
              }}
            >
              {STATUSES.map((s) => (
                <option value={s}>{s === "" ? "Any" : s}</option>
              ))}
            </select>
          </div>
          <div class="md:col-span-2">
            <label class="label" for="gu-filter">
              Global user ID
            </label>
            <input
              id="gu-filter"
              class="input font-mono text-xs"
              placeholder="UUID"
              value={globalUserId()}
              onInput={(e) => {
                setGlobalUserId(e.currentTarget.value.trim());
                pages.reset();
              }}
            />
          </div>
        </div>
      </div>

      <div class="card">
        <AsyncResource resource={page}>
          {(data) => (
            <>
              <table class="data-table">
                <thead>
                  <tr>
                    <th>Instance</th>
                    <th>Status</th>
                    <th>Balance</th>
                    <th>P&amp;L</th>
                    <th>Started</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  <For each={data().challenges}>
                    {(c) => (
                      <tr>
                        <td class="font-mono text-xs">
                          {c.instance_id.slice(0, 8)}…
                        </td>
                        <td>
                          <span class={statusClass(c.status)}>{c.status}</span>
                        </td>
                        <td class="font-mono">{c.initial_balance}</td>
                        <td class="font-mono">{c.total_pnl ?? "—"}</td>
                        <td class="text-slate-500">
                          {new Date(c.started_at).toLocaleString()}
                        </td>
                        <td>
                          <A
                            class="text-sm underline"
                            href={`/challenges/${c.instance_id}`}
                          >
                            View
                          </A>
                        </td>
                      </tr>
                    )}
                  </For>
                  {data().challenges.length === 0 && (
                    <tr>
                      <td colSpan={6} class="text-center text-slate-500 py-6">
                        No {words()?.runs} match these filters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              <div class="card-body">
                <CursorPager pages={pages} next={data().next_cursor} />
              </div>
            </>
          )}
        </AsyncResource>
      </div>
    </>
  );
}

function statusClass(status: string): string {
  switch (status) {
    case "active":
      return "status-active";
    case "passed":
      return "status-passed";
    case "failed":
      return "status-failed";
    case "pending_review":
      return "status-pending";
    case "suspended":
      return "status-suspended";
    default:
      return "status-neutral";
  }
}
